/**
 * @documental
 * @export Ssn
 * @name
 * Table.Ssn
 */
import { FC } from "react";

import { BaseColumnProps } from "../BaseColumnProps";
import { ColumnElementConstructor } from "../ColumnElementConstructor";
import { Custom } from "./Custom";

/**
 * The `Ssn` component properties.
 */
export interface SsnProps extends Omit<BaseColumnProps, "align"> {
  /**
   * If true, only the last four digits of the SSN will be shown, with the rest masked as '*'.
   * If false or undefined, the full SSN will be shown (formatted as XXX-XX-XXXX).
   */
  readonly mask?: boolean;
}

/**
 * Formats a string or number as an SSN (XXX-XX-XXXX), optionally masking all but the last four digits.
 */
function formatSsn(value: string | number, mask: boolean = false): string {
  const ssn = String(value).replace(/[^\d]/g, "");
  if (ssn.length !== 9) return "--";
  if (mask) {
    return `***-**-${ssn.slice(5)}`;
  }
  return `${ssn.slice(0, 3)}-${ssn.slice(3, 5)}-${ssn.slice(5)}`;
}

/**
 * Column which displays a formatted SSN, optionally masking all but the last four digits.
 */
export const SSN: FC<SsnProps> = (props) => {
  const { mask = false } = props;

  return (
    <Custom {...props}>
      {(value) => {
        if (value === null || value === undefined) return "--";
        return formatSsn(value as string | number, mask);
      }}
    </Custom>
  );
};

(SSN as ColumnElementConstructor<SsnProps>).className = "ssn";
(SSN as ColumnElementConstructor<SsnProps>).headerAlignment = "left";
